import env from "@/app/config/env";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import axios from "axios";
import Keycloak from "keycloak-js";
import { createContext, useContext, useMemo } from "react";
import { useKeycloak } from "./keycloak-hooks";

type ContextKeycloakType = ReturnType<typeof useKeycloak>;
export const keycloakContext = createContext<ContextKeycloakType | null>(null);

export function useContextKeycloak() {
  const context = useContext(keycloakContext);

  if (!context) {
    throw new Error(
      "useContextKeycloak must be used within a ContextKeycloakProvider",
    );
  }

  return context;
}

function KeycloakProvider({ children }: { children: React.ReactNode }) {
  const keycloakValue = useKeycloak();

  const { keycloakUserState } = keycloakValue;

  if (
    keycloakUserState === "init" ||
    (keycloakUserState === "authorize" && !keycloakValue.jwtToken)
  ) {
    return null;
  }
  return (
    <keycloakContext.Provider value={keycloakValue}>
      {children}
    </keycloakContext.Provider>
  );
}

export function ContextKeycloakProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const keycloak = useMemo(() => new Keycloak(env.keycloak), []);

  const onUpdateTokens = ({ token }: { token?: string }) => {
    axios.interceptors.request.use((config: any) => {
      const sessionToken = sessionStorage.getItem("login-token");
      config.headers.Authorization = token
        ? `Bearer ${token}`
        : sessionToken
          ? `Bearer ${sessionToken}`
          : undefined;
      return config;
    });
  };

  const initOptions = useMemo(
    () => ({
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      checkLoginIframe: false,
      enableLogging: false,
    }),
    [],
  );

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      onTokens={onUpdateTokens}
      initOptions={initOptions}
    >
      <KeycloakProvider>{children}</KeycloakProvider>
    </ReactKeycloakProvider>
  );
}
